/**
 * Keeps the interface language in step with the preference (Phase 7, Lane I).
 *
 * The language is the account's `locale` preference (prefs.tsx keeps it on the server
 * and caches it in this browser), or the browser's language when none is set. main.tsx
 * starts in the cached one, so the first paint is already in the right language; when
 * the account's arrives and differs, or someone picks another in Preferences, the
 * German catalog is loaded (its own chunk) and the pages below are re-mounted, so every
 * screen reads its words again. A failed load keeps the current language.
 */
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { usePrefs } from '../prefs';
import { browserLocale, getLocale, loadLocale, setLocale } from './index';

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { prefs } = usePrefs();
  const wanted = prefs.locale ?? browserLocale();
  const [shown, setShown] = useState(getLocale);

  useEffect(() => {
    if (wanted === getLocale()) {
      setShown(wanted);
      return;
    }
    let live = true;
    loadLocale(wanted)
      .then(() => {
        if (!live) return;
        setLocale(wanted);
        setShown(wanted);
      })
      .catch(() => {
        /* the catalog could not be loaded (offline, say): stay in the current language */
      });
    return () => {
      live = false;
    };
  }, [wanted]);

  return <Fragment key={shown}>{children}</Fragment>;
}

/**
 * The language to start in, before React renders: this browser's copy of the
 * preferences, else the browser's language.
 */
export function initialLocale(): 'en' | 'de' {
  try {
    const raw = window.localStorage.getItem('studio.prefs');
    const locale = raw ? (JSON.parse(raw) as { locale?: unknown }).locale : undefined;
    if (locale === 'en' || locale === 'de') return locale;
  } catch {
    /* storage refused or unreadable: the browser's language */
  }
  return browserLocale();
}
